import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { runBrowserSync, type BrowserSyncProgress, type BrowserSyncScope } from '@/lib/browser-sync';

export function useBrowserSync() {
  const queryClient = useQueryClient();
  const controllerRef = useRef<AbortController | null>(null);
  const [progress, setProgress] = useState<BrowserSyncProgress | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => () => {
    controllerRef.current?.abort();
  }, []);

  const start = async (scope: BrowserSyncScope = 'full') => {
    if (controllerRef.current) {
      return { status: 'running' as const, counts: progress?.counts ?? {}, errors: [] };
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setIsRunning(true);
    setProgress(null);

    try {
      const result = await runBrowserSync({
        scope,
        signal: controller.signal,
        onProgress: (nextProgress) => {
          setProgress(nextProgress);
        },
      });

      return result;
    } finally {
      controllerRef.current = null;
      setIsRunning(false);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['products'] }),
        queryClient.invalidateQueries({ queryKey: ['orders'] }),
        queryClient.invalidateQueries({ queryKey: ['customers'] }),
        queryClient.invalidateQueries({ queryKey: ['inventory'] }),
        queryClient.invalidateQueries({ queryKey: ['sync-logs'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] }),
        queryClient.invalidateQueries({ queryKey: ['connection'] }),
        queryClient.invalidateQueries({ queryKey: ['settings'] }),
      ]);
    }
  };

  const cancel = () => {
    controllerRef.current?.abort();
  };

  return {
    cancel,
    isRunning,
    progress,
    start,
  };
}
